<template>
    <div v-show="modelValue">
        <div v-show="!default_open" class="b2b-express-order__cta-float-bar">
            <div class="summary-container">
                <div class="summary">
                    <div class="summary-text">
                        <p class="paragraph-default">Use Express Ordering to view item variations, UPC, Weight, Pricing, Quantity
                            and ETA and place an order - Click the button or hit “Enter”</p>
                    </div>
                </div>
                <RothcoCta @click="hide" class="secondary dark" aria-label="Hide Express Ordering">Hide Express Ordering</RothcoCta>
            </div>
        </div>

        <div class="flex-row-forced justify-between align-end express-header">
            <slot />
            <div class="input-folder mobile-hidden">
                <div class="input-folder--input">
                    <number-input id="applyToAllQuantity" v-model.natural.whole="applyToAllQuantity" aria-placeholder="Apply Quantity to all items" placeholder=" " :min="0"/>
                    <label
                        for="applyToAllQuantity"
                    >
                        <p class="paragraph-default">Apply to Lines</p>
                    </label>
                </div>    
                <div class="input-folder--support">
                    <p class="paragraph-default">Press enter to apply quantity to checked rows</p>
                </div>
            </div>
        </div>
        <div class="express-color-swatch" v-if="numberOfColors > 0">
            <p id="color-filter-label">Filter by color by selecting below</p>
            <div class="product-card__swatches-container" role="group" aria-labelledby="color-filter-label">
                <div @click="prevColorPage" v-if="currentColorPage > 0" class="product-card__swatches-more" tabindex="0" style="gap: 0; width: 1.7rem; cursor: pointer;">
                    <SwatchesMoreIcon alt="swatch-color-arrow" class="icon-flipped" aria-hidden="true"/>
                </div>
                <div class="product-card__swatches hide-subsequent-rows">
                    <div class="product-card__swatch all-color" @click="selectedColor = ''"
                        role="button"
                        tabindex="0"
                        aria-label="Show all colors"
                        :aria-pressed="`${selectedColor ? 'false' : 'true'}`"
                    >
                        <div :class="`product-card__swatches-color ${selectedColor ? '' : 'active'}`">
                            All Colors
                        </div>
                    </div>

                    <div class="product-card__swatch" v-for="(color, index) in paginatedColors" :key="index" @click="selectedColor = color"
                            role="button"
                            tabindex="0"
                            :aria-label="`Filter by ${color}`"
                            :aria-pressed="`${selectedColor == color ? 'true' : 'false'}`"
                        >
                        <div :class="{
                            'product-card__swatches-color': true,
                            'active': index == paginatedColors.findIndex(c=>c == selectedColor),
                            'selected':  itemsHaveQuantityByColor[color] > 0,
                            }">
                            <img 
                                :alt="`select ${color} swatch color`"
                                :title="color" 
                                loading="lazy"
                                class="swatch-image" 
                                :src="optimize(swatch_images[color])" 
                                tabindex="0"
                            />
                        </div>
                    </div>
                </div>
                <div @click="nextColorPage" v-if="numberOfColors > itemsPerPage && currentColorPage < totalColorPages - 1" class="product-card__swatches-more" 
                        role="button"
                        tabindex="0"
                        aria-label="Show more colors"
                        :style="currentColorPage == 0 ? `gap: ${numberOfColors - itemsPerPage > 9 ? '1' : '5'}px; cursor: pointer;` : 'gap: 0; width: 1.7rem; cursor: pointer;'">
                        <div class="product-card__swatches-colors-count" v-if="currentColorPage == 0">
                            +{{ numberOfColors - itemsPerPage }}
                        </div>
                        <SwatchesMoreIcon alt="swatch-color-arrow" aria-hidden="true"/>
                </div>
            </div>
        </div>
        <rothco-table
            :pt="{
                root: 'b2b-express-order__table-content'
            }"
            id="direct_ordering_table"
            :visible-fields="visibleFields"
            v-model:content="filteredGridItems"
            v-model:focused="focused"
            primary-key="item_variation_id"
            :useVirtualRows="true"
            focusable
            :checkable="applyToAllQuantity > 0"
            v-model:checkedRows="checkedRows"
            :row-is-checkable="hasInventory"
        >

            <template v-for="(item, index) in filteredGridItems" :key="`item_no_${index}`" v-slot:[`item_no_${index}`]="slotProps">
                <a @click.prevent="openProductImageModal(item.data?.primary_image?.image_filename)" href="#" title="View Product Image"
                    :class="`${(slotProps.header.show_only == 'mobile') ? 'card-title' : 'table-name'}`">
                    {{ item.data.item_no }}
                </a>
            </template>

            <template v-for="(item, index) in filteredGridItems" :key="`variant_${index}`" v-slot:[`variant_${index}`]="slotProps">
                <div class="badge-wrapper">
                    <p v-if="Object.keys(item?.data?.text_description ?? {}).length === 0" class="paragraph-default badge" style="visibility: hidden">No Variants</p>
                    <p v-for="(variantValue,i) in (item.data.text_description ?? {})" :key="i" class="paragraph-default badge">{{ variantValue }}</p>
                </div>
            </template>

            <template v-for="(item, index) in filteredGridItems" :key="`price_${index}`" v-slot:[`price_${index}`]="slotProps">
                <p class="paragraph-default desk-text-right grid-price">
                    <span>{{ !canSeePricing ? "---" : (item.data.current_unit_price.price) ? `${formatPriceAsString(+item.data.current_unit_price.price)}` : "---" }}</span>
                    <s class="grid-origin-price" v-if="canSeePricing && item.data.wholesale_price && item.data.current_unit_price.price < item.data.wholesale_price
                    ">Orig: {{ (item.data.formattedWholesalePrice) ? (item.data.formattedWholesalePrice) : "---" }}</s>
                </p>
            </template>

             <template v-for="(item, index) in filteredGridItems" :key="`case_pack_${index}`" v-slot:[`case_pack_${index}`]="slotProps">
                <p class="paragraph-default desk-text-right">
                    {{ item.data?.next_case?.min_order_units ? item.data.next_case?.min_order_units : "" }}
                </p>
            </template>

            <template v-for="(item, index) in filteredGridItems" :key="`case_price_${index}`" v-slot:[`case_price_${index}`]="slotProps">
                <p class="paragraph-default desk-text-right">
                {{ !canSeePricing ? "---" : item?.data.next_case?.min_order_units != "N/A" ? `${formatPriceAsString(+item.data.next_case.price)}` : "N/A" }}
                </p>
            </template>

            <template v-for="(item, index) in filteredGridItems" :key="`inventory_level_${index}`" v-slot:[`inventory_level_${index}`]="slotProps">
                <span v-if="slotProps.header.show_only == 'mobile'">
                    <p class="paragraph-caption">{{ item.data.inventory_level ? item.data.inventory_level > 99 || item.data.inventory_level == '99+' ? '99+' : item.data.inventory_level : '---' }}</p>
                </span>
                <span v-else>
                    <a v-if="!hasInventory(item.data) && hasETADate(item.data)" href="#" class="get-notified" @click.prevent="openEmail(item.data.item_variation_id)">Get Notified</a>
                    <p v-if="!hasInventory(item.data)" class="paragraph-caption">
                        {{ hasETADate(item.data) ? `ETA ${moment(item.data.eta_date).format('MM/DD/YY')}` : '---' }}
                    </p>
                    <p v-else class="paragraph-default">
                        {{ item.data.inventory_level > 99 ? '99+' : item.data.inventory_level }}
                    </p>
                </span>
            </template>
            
            <template
                v-for="(row, index) in filteredGridItems"
                :key="`quantity_${index}`"
                v-slot:[`quantity_${index}`]="slotProps"
            >
                    <input
                        :id="`desktop_quantity_input_${index}`"
                        v-model="filteredGridItems[index].data.quantity"
                        @input="recalculateItemPricing(row.data.item_variation_id);recalculateTotals(gridItems)"
                        type="number"
                        :class="{'table-input mob-text-left desk-text-center': true, 'invalid-state-input': !validateQuantity(filteredGridItems[index].data.quantity)}"
                        :min="0"
                        :disabled="!hasInventory(row.data) || !row.data.current_unit_price.price"
                    />
            </template>

            <template v-for="(item, index) in filteredGridItems" :key="`cost_${index}`" v-slot:[`cost_${index}`]="slotProps">
                <p class="paragraph-default desk-text-right">
                    {{ !canSeePricing ? "---" : (item.data.quantity && item.data.current_unit_price.price) ? ('$' + formatTotalPrice(item.data.current_unit_price.price, item.data.quantity)) : "---" }}
                </p>
            </template>
        </rothco-table>
        <div class="b2b-express-order__information">
			<div class="b2b-express-order__information--cta">
				<div class="total-wrapper">
					<div class="total">
						<p class="title-s">Total</p>
						<p class="paragraph-default">{{ canSeePricing ? ('$' + totalPrice) : '---' }}</p>
					</div>
					<div class="qty">
						<p class="title-s">Qty</p>
						<p class="paragraph-default">{{ totalQuantity }}</p>
					</div>
				</div>	
                <RothcoCta v-if="+totalQuantity > 0" type="submit" class="primary" aria-label="Add selected items to cart" @click="add" :disabled="isAdding">Add to Cart</RothcoCta>		
			</div>

			<div class="b2b-express-order__information--text">
				<h5 class="title-s">Rothco Express Ordering</h5>
				<p class="paragraph-details" id="inventory_disclaimer_text">
					If an item is out of stock, it will show an ETA in the QTY field. If it does not have an ETA, then
					the item is discontinued. The Express Ordering applications does not allow for automatic back
					ordering of an item. If you would like to place a back order, please contact Rothco directly at <a
						:href="`tel:${orderingInfo.contacts.phone.replace('-', '')}`">{{ orderingInfo.contacts.phone }}</a> or <a
						:href="`mailto:${orderingInfo.contacts.email}`">{{ orderingInfo.contacts.email }}</a> All orders larger than {{ orderingInfo.line_limit }} SKU'S cannot be
					placed via our Express Ordering System, please separate your orders to keep them below this size.
				</p>
			</div>

		</div>
        <Teleport to="body" v-if="modelValue" :key="stickyBarKey">
            <div id="express_ordering_bottom_sticky" class="b2b-express-order__cta-sticky-bar">
                <a class="express-ordering-toggle" v-if="default_open == false" @click="modelValue = false" >Hide Express Ordering</a>
                <div class="summary-container">
                    <div class="summary">
                        <div class="summary-text">
                            <p class="title-s">Qty</p>
                            <p class="paragraph-default">{{ totalQuantity }}</p>
                        </div>
                        <div class="summary-text">
                            <p class="title-s">Total</p>
                            <p class="paragraph-default">{{ canSeePricing ? ('$' + totalPrice) : '---' }}</p>
                        </div>
                    </div>
                    <button v-if="+totalQuantity > 0" class="secondary white small" aria-label="proceed to Checkout" @click="add" :disabled="isAdding">
                        Add to Cart
                        <span aria-hidden="true" class="icon">
                            <CarretsWhite alt="arrow right" />
                        </span>
                    </button>
                </div>
            </div>
        </Teleport>
    </div>
</template>
<script lang="ts" setup>
    import RothcoTable from "@/components/RothcoTable.vue";
    import RothcoCta from "@/components/RothcoCta.vue"
    import ReceiveEmailModal from "@/components/ReceiveEmailModal.vue";
    import ImagePreviewModal from "@/components/ImagePreviewModal.vue";
    import CartPreview from "@/components/cart/CartPreview.vue";
    import SwatchesMoreIcon from "@icons/swatches-arrow-more.svg";
    import {
        useDateFormat,
        useMagicKeys,
        useActiveElement,
        useFocus
    } from "@vueuse/core";
    import { ref, computed, onMounted, toRefs, watch, nextTick, h } from "vue";
    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";
    import { useOrderingItemsGrid } from "@/helpers/composables/useOrderingItemsGrid";
    import { useDialog } from "primevue/usedialog";
    import moment from "moment";
    import { formatTotalPrice, formatPriceAsString } from "@/helpers/composables/usePriceFormatting";
    import CarretsWhite from "@icons/carrets-white.svg";
    import { useTypeAheadStore } from "@/store/typeAhead";
    import { router, usePage } from "@inertiajs/vue3";
    import { comingSoonImages } from "@/helpers/composables/comingSoonImages"
    import { productUploadImageUrl } from "@/helpers/productUploadImageUrl";
    import useImageOptimization from "@/helpers/composables/useImageOptimization";
    import { useRothcoBreakpoints } from "@/helpers/composables/useBreakpoints";
    
    let dialog = useDialog();
    let modelValue = defineModel();
    let settings = useSettingsStore();
    let { userInfo, is_dealer, canSeePricing } = storeToRefs(settings);

    let emailOpenStatus = ref(false);
    let openEmail = function (itemVariationId) {
        dialog.open(ReceiveEmailModal,{
            data: {
                itemVariationId
            }
        });
    };

    let currentColorPage = ref(0);

    let breakpoints = useRothcoBreakpoints();

    const itemsPerPage =  computed(() => {
        if(breakpoints.smallerOrEqual('tablet').value) {
            return 7;
        } else if(breakpoints.smallerOrEqual('laptop').value) {
            return 9;
        }

        return 9;
    });

    let selectedColor = ref('');

    let { optimize } = useImageOptimization();

    let uniqueColors = computed(() => {
        const seen = new Set<string>();
        return gridItems
            .map(item => item.data.text_description?.Color)
            .filter((value): value is string => {
                if (!value || seen.has(value)) {
                    return false;
                }
                seen.add(value);
                return true;
        });
    });


    let paginatedColors = computed(()=>{
        const start = currentColorPage.value * itemsPerPage.value;
        const end = start + itemsPerPage.value;
        return uniqueColors.value.slice(start, end);
    });

    let totalColorPages = computed(()=>{
        return Math.ceil(numberOfColors.value / itemsPerPage.value);
    });

    const numberOfColors = computed(()=>{
        return Math.ceil(uniqueColors.value?.length > 0 ? uniqueColors.value.length : 0);
    });

    function nextColorPage(){
        if (currentColorPage.value < totalColorPages.value - 1) {
            currentColorPage.value++;
        }
    }

    function prevColorPage(){
        if (currentColorPage.value > 0) {
            currentColorPage.value--;
        }
    }
    
    let filteredGridItems = computed(() => {
        return gridItems.filter(item =>{
            return !selectedColor.value || item.data.color == selectedColor.value;
        });
    });

    function toSnakeCase(str) {
        return str.toLowerCase().replace(/\s+/g, "_");
    }
    let productSpecs = computed(() => {
        let specs = [];
        if (itemVariations.value.length) {
            for (let spec of Object.keys(
                itemVariations.value[0].text_description
            )) {
                specs.push({ 
                        label: spec, 
                        field: toSnakeCase(spec),
                        is_custom: false,
                        show_only: 'desktop',
                        pt: {
                            headerCell: { class: 'title-xs text-left' },
                            bodyCell: { class: 'mob-w-25 text-left', 'data-label': "Item" },
                        }
                });
            }
        }
        return specs;
    });
    let visibleFields = computed(() => {
        return [
            { 
                label: "Item #", field: "item_no",
                is_custom: true,
                pt: {
                    headerCell: { class: 'title-xs text-left' },
                    bodyCell: { class: 'mob-w-25 text-left', 'data-label': "Item" },
                }
            },
            ...productSpecs?.value,
            {
                label: "Variation",
                field: "variant",
                is_custom: true,
                show_only: 'mobile',
                pt: {
                    bodyCell: { class: 'mob-w-75', 'data-label': "Variation" }
                }
            },
            { 
                label: "UPC", 
                field: "upc",
                show_only: 'desktop',
                is_custom: false,
                pt: {
                    headerCell: { class: 'title-xs text-left' },
                    bodyCell: { class: 'text-left' },
                }
             },
             {
                label: "Wgt. (lbs)",
                field: "formattedWeight",
                is_custom: false,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                    bodyCell: { class: 'text-left' },
                    bodyCellContent: { class: 'paragraph-default text-center'}
                }
            },
            {
                label: "MSRP",
                field: "formattedMsrp",
                is_custom: false,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'title-xs text-right' },
                    bodyCellContent: { class: 'paragraph-default text-right'}
                }
            },
            { 
                label: "MAP",
                field: "formattedMap",
                is_custom: false,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'title-xs text-right' },
                    bodyCellContent: { class: 'paragraph-default text-right'}
                }
            },
            {
                label: "Avail.",
                field: "inventory_level",
                is_custom: true,
                show_only: 'mobile',
                pt: {
                    headerCell: { class: 'title-xs text-left' },
                    bodyCell: (state)=>{
                        return {
                            class: 'mob-w-25 mobile-only', 
                            'data-label': "Available" ,
                            colspan: 1
                        }
                    }
                }
            },
            {
                label: "Price",
                field: "price",
                is_custom: true,
                pt: {
                    headerCell: { class: 'title-xs text-right' },
                    bodyCell: { class: 'mob-w-25', 'data-label': "Unit Price"},
                    bodyCellContent: { class: 'paragraph-default desk-text-right mob-text-left', 'data-label': "Unit Price" }
                }
            },
            { 
                label: "UOM", 
                field: "uom" ,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                    bodyCellContent: { class: 'paragraph-default text-center'}
                }
            },
            {
                label: "Case Pack",
                field: "case_pack",
                is_custom: true,
                show_only: 'desktop',
                visible: computedCasePricing.value,
                pt: {
                    headerCell: { class: 'title-xs text-left' },
                    bodyCell: { class: 'text-left' }
                }
            },
            {
                label: "Case Price",
                field: "case_price",
                is_custom: true,
                show_only: 'desktop',
                visible: computedCasePricing.value && canSeePricing.value,
                pt: {
                    headerCell: { class: 'title-xs text-right' },
                    bodyCell: { class: 'paragraph-default text-right' }
                },
            },
            
            {
                label: "Avail.",
                field: "inventory_level",
                is_custom: true,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'title-xs text-left' },
                    bodyCell: (state)=>{
                        return {
                            class: 'mob-w-25 desktop-only', 
                            'data-label': "Available" ,
                            colspan: +state?.instance?.rowData?.data?.inventory_level == 0 ? 2 : 1
                        }
                    }
                }
            },
            // { label: "Avail.", field: "formattedAvailable" },
            { 
                label: "QTY", 
                field: "quantity", 
                is_custom: true ,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                    bodyCell: (state) => {
                        return {
                            class: ['qty-cell', (+state?.instance?.rowData?.data?.inventory_level == 0 ? 'hide-column' : 'desktop-only')], 
                            'data-label': "Qty" ,
                            colspan: +state?.instance?.rowData?.data?.inventory_level == 0 ? 0 : 1,
                            style: +state?.instance?.rowData?.data?.inventory_level == 0 ? 'display:none;' : ''
                        }
                    }
                }
            },
            {
                label: "Cost",
                field: "cost",
                is_custom: true,
                show_only: "mobile",
                pt: {
                    headerCell: { class: 'desktop-only title-xs text-right' },
                    bodyCell: { class: 'desktop-only text-right', 'data-label': "Cost" }
                }
            },
            {
                label: "Qty",
                field: "quantity",
                is_custom: true,
                show_only: "mobile",
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                    bodyCell: { class: 'mob-w-25 qty-cell mob-text-left', 'data-label': "Qty"}
                }
            },
            {
                label: "Cost",
                field: "cost",
                is_custom: true,
                show_only: "desktop",
                pt: {
                    headerCell: { class: 'desktop-only title-xs text-right' },
                    bodyCell: { class: 'desktop-only text-right' }
                }

            }
        ].filter((field) => field.disable != true);
    });

    let props = defineProps({
        productLineCode: {
            type: String,
            required: true
        },
        item_variations: {
            type: Array,
            required: true,
            default() {
                return [];
            }
        },
        swatch_images: {
            type: Array,
            required: true,
            default() {
                return [];
            }
        },
        prices: {
            type: Array,
            required: true,
            default() {
                return [];
            }
        },
        default_open: {
            type: Boolean,
            default: false
        },
        currentListItems: {
            type: Array,
            default() {
                return [];
            }
        },
        source: {
            type: String,
            default: null
        },
        current_list_id: {
            type: [Number, String],
            default: ""
        },
        orderingInfo: {
            type: Object,
            default() {
                return {
                    line_limit: 0,
                    contacts: {
                        email: "",
                        phone: ""
                    }
                };
            }
        }
    });

    let {
        item_variations,
        swatch_images,
        prices,
        default_open,
        currentListItems,
        source,
        current_list_id
    } = toRefs(props);

    let itemVariations = computed(() => {
        return item_variations.value.map((item) => {
            let newItem = JSON.parse(JSON.stringify(item));
            let pricingProxy = prices.value.find(
                (price) => price.web_variation_id == item.item_variation_id
            );
            if (pricingProxy) {
                pricingProxy = JSON.parse(JSON.stringify(pricingProxy));
            }
            let pricing = pricingProxy ?? { case_prices: [] };
            let currentListItem = currentListItems.value.find(
                (listItem) =>
                    listItem.item_variation_id == item.item_variation_id
            );
            if (pricing.case_prices instanceof Object) {
                pricing.case_prices = Object.values(pricing.case_prices);
            }
            newItem.pricing = { ...pricing };
            newItem.listItem = currentListItem;
            newItem.product_line_code = props.productLineCode;
            return newItem;
        });
    });

    let productLineCodes = computed(()=>{
        return item_variations.value.reduce((acc, item) => {
            acc.add(item.product_line_code);
            return acc;
        }, new Set<string>([props.productLineCode]));
    });

    const {
        recalculatePricing,
        recalculateTotals,
        gridItems,
        setGridItemsQuantity,
        hasCasePricing,
        totalPrice,
        totalQuantity,
        addToList,
        validateQuantity,
        stickyBarKey,
        clearGridItemsQuantity
    } = useOrderingItemsGrid({
        prices: prices,
        items: itemVariations.value,
        formatters: new Map(
            Object.entries({
                ...productSpecs.value.reduce((acc, spec) => {
                    acc[spec.field] = (item) =>
                        item.text_description[spec.label];
                    return acc;
                }, {}),
                formattedWeight: (item) =>
                    `${parseFloat(item.weight.toString()).toFixed(2)}`,
                formattedMsrp: (item) =>
                    `$${parseFloat((item.pricing?.msrp_price ?? 0).toString()).toFixed(2)}`,
                formattedMap: (item) =>
                    `$${parseFloat((item.pricing?.map_price ?? 0).toString()).toFixed(2)}`,
                formattedUnitPrice: (item) =>
                    `$${parseFloat((item.current_unit_price?.price ?? 0).toString()).toFixed(2)}`,
                formattedWholesalePrice: (item) =>
                    item.wholesale_price && canSeePricing.value
                        ?  `${formatPriceAsString(item.wholesale_price)}`
                        : "---",
                formattedCost: (item) =>
                    `$${formatTotalPrice((item.current_unit_price?.price ?? 0), item.quantity)}`,
                formattedEtaDate: (item) => {
                    let eta = new Date(item.eta_date);
                    eta.setDate(eta.getDate() + 3);
                    return `ETA: ${useDateFormat(eta, "MM/DD/YYYY").value.replace(/\"/g, "")}`;
                },
                formattedCaseQuantity: (item) => {
                    return item?.next_case?.min_order_units?.toLocaleString?.();
                },
                formattedCasePrice: (item) =>
                    item?.next_case?.min_order_units == "N/A"
                        ? "N/A"
                        : `$${parseFloat(item.next_case?.price?.toString?.()).toFixed(2)}`,
                formattedAvailable: (item) =>
                    item.inventory_level > 99
                        ? "99+"
                        : item.inventory_level.toString()
            })
        ),
        source: source.value
    });

    onMounted(() => {
        modelValue.value = default_open.value;
    });

    let focused = ref(false);
    const isAdding = ref(false);
    const { shift, enter } = useMagicKeys();
    const activeElement = useActiveElement();

    let typeAheadFocusedNotFocused = computed(() => {
        return activeElement.value?.id == "typeAheadInput";
    });
    function getIndexFromId(id) {
        const match = id.match(/desktop_quantity_input_(\d+)/);
        if (match) {
            return parseInt(match[1], 10);
        }
        return null;
    }
    const availableRows = computed(() => {
        return filteredGridItems.value
            .map((row, index) => {
                if (row.data.inventory_level > 0) {
                    return { row_data: row, index };
                }
            })
            .filter(Boolean);
    });

    watch(enter, (v) => {
        if (v) {
            if (focused.value) {
                const currentId = activeElement.value?.id;
                const currIdIndex = getIndexFromId(currentId);

                const currAvIndex = availableRows.value.indexOf(
                    availableRows.value.find((row) => row.index == currIdIndex)
                );

                if (
                    shift.value &&
                    getIndexFromId(currentId) != null &&
                    currAvIndex > 0
                ) {
                    const prev = filteredGridItems.value.indexOf(
                        availableRows.value[currAvIndex - 1].row_data
                    );
                    const el = document.getElementById(
                        `desktop_quantity_input_${prev}`
                    );
                    el.focus();
                } else if (
                    !shift.value &&
                    getIndexFromId(currentId) != null &&
                    currAvIndex < availableRows.value.length - 1
                ) {
                    const next = filteredGridItems.value.indexOf(
                        availableRows.value[currAvIndex + 1].row_data
                    );

                    const el = document.getElementById(
                        `desktop_quantity_input_${next}`
                    );
                    el.focus();
                }
            } else {
                let activeElement = document.activeElement;
                if (activeElement?.id != "typeAheadInput" && is_dealer.value) {
                    modelValue.value = true;
                    document
                        .getElementById("direct_ordering_table")
                        ?.scrollIntoView?.();
                }
            }
        }
    });

    let { open } = useTypeAheadStore();
    function add() {
        if (isAdding.value) return;
        isAdding.value = true;

        let listId = current_list_id.value;
        addToList(listId, {
            afterFetch: () => {

               isAdding.value = false;
                if(!default_open.value){
                    modelValue.value = false;
                }
                else{
                    clearGridItemsQuantity();
                    applyToAllQuantity.value = null;
                    checkedRows.value = [];
                    document.querySelectorAll('[id^=typeAheadSearch-]').forEach((el) => {
                        el.value = '';
                        el.focus();
                    });
                    open();
                    
                }
                router.reload({
                    only: ["current_list", "last_added_item"],
                    onSuccess: () => {
                        settings?.setList(usePage().props.current_list);
                        openCartPreview();
                    }
                });
            }
        });
    }

    let openCartPreview = () => {
        const dialogRef = dialog.open(CartPreview, {
            props: {
                showHeader: false,
                modal: false
            }
        });

        setTimeout(() => {
            dialogRef.close();
        }, 9000);
    };

    let computedCasePricing = computed(() => {
        return props.prices.filter(price =>{
            return price.case_prices?.filter(price => price.min_order_units > 1)?.length > 0
        }).length > 0;
    });

    const recalculateItemPricing = (item_variation_id) => {
        let index = gridItems.findIndex((gridItem) => gridItem.data.item_variation_id == item_variation_id);
        recalculatePricing(index);
    }

    const hasInventory = (row)=>{
        return +row.inventory_level > 0 || row.inventory_level === '99+'
    }

    const hasETADate = (row) => {
        return row.eta_date ?? false;
    }

    function hide() {
        modelValue.value = false;
    }

    let itemsHaveQuantityByColor = computed(() => {
        return gridItems.reduce((acc, item) => {
            if (item.data.color) {
                acc[item.data.color] = acc[item.data.color] || 0;
                acc[item.data.color] += item.data.quantity || 0;
            }
            return acc;
        }, {});
    });

    const openProductImageModal = (image)=>{
        if(image){
            const imageUrl = image
                ? productUploadImageUrl(image, null)
                : comingSoonImages().small
            dialog?.open(ImagePreviewModal,{
                props: {
                    showHeader: false,
                    modal: true,
                },
                data: {
                    image_url: imageUrl
                }
            });
        }else{
            store.setNotificationMessage({
                message: "Image preview unavailable for this product.",
                type: "error"
            })
        }
    }
    let applyToAllQuantity = ref(null);
    let checkedRows = ref([]);

    let activeCheckedRows = computed(()=>{
        const activeIds = new Set(filteredGridItems.value.map(item => item.data.item_variation_id));
        return checkedRows.value.filter(row => activeIds.has(row.data.item_variation_id));
    });

    let ignoreCheckedRowsChange = false;
    let ignoreApplyToAllQuantityChange = false;

    watch(selectedColor, (newValue) => {
        ignoreCheckedRowsChange = true;
        checkedRows.value = [];

        if(applyToAllQuantity.value !== null) {
            ignoreApplyToAllQuantityChange = true;
            applyToAllQuantity.value = null;
        }
    })
    
    watch(applyToAllQuantity, (newValue) => {
        if(ignoreApplyToAllQuantityChange) {
            ignoreApplyToAllQuantityChange = false;
            return;
        }

        if(checkedRows.value.length == 0) {
            checkedRows.value = filteredGridItems.value.filter((item) => hasInventory(item.data));
        }
        checkedRows.value.forEach((item) => {
            let index = gridItems.findIndex((gridItem) => gridItem.data.item_variation_id == item.data.item_variation_id);
            if (index > -1) {
            gridItems[index].data.quantity = newValue > 0 ? newValue : 0;
            recalculatePricing(index);
            }
        });

        recalculateTotals(gridItems)
        
    });

    watch(checkedRows, (newValue, oldValue) => {
        if(ignoreCheckedRowsChange) {
            ignoreCheckedRowsChange = false;
            return;
        }
     
       for(let index = 0; index < gridItems.length; index++){
            let item = gridItems[index];
            let isNewChecked = newValue.find((newItem) => newItem.data.item_variation_id == item.data.item_variation_id);
            let isOldChecked = oldValue.find((oldItem) => oldItem.data.item_variation_id == item.data.item_variation_id);

            let isInActiveScreen = filteredGridItems.value.find((item) => item.data.item_variation_id == item.data.item_variation_id);

            if(!isInActiveScreen) {
                continue;
            }

            if(isNewChecked && !isOldChecked){
                item.data.quantity = applyToAllQuantity.value;
            }else if(!isNewChecked && isOldChecked){
                item.data.quantity = 0;
            }
            recalculatePricing(index);
       }
       recalculateTotals(gridItems);
    });

</script>
